import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * チャージ（デポジット）請求額の内訳。
 *
 * BE `payments.deposit_fees.compute_deposit_quote` の出力そのまま。
 * 金額はすべて税抜・整数円の文字列 Decimal（BE の慣例）。
 * FE 側では再計算せず、この値を表示にそのまま使う（表示額と実際の
 * Stripe 請求額のズレを構造的に防ぐ）。
 */
export interface DepositQuote {
  /** チャージ額（税抜）。成功時に balance_free へ加算される額 */
  charge_amount: string;
  /** 利用料率（% 表記。例: "17.5"） */
  usage_fee_rate: string;
  /** サービス利用料（税抜） */
  usage_fee: string;
  /** 消費税（チャージ額 + 利用料に対する 10%） */
  tax: string;
  /** カード請求額 */
  total: string;
  /** 消費税率（"0.10"） */
  tax_rate: string;
}

export interface DepositQuoteResponse {
  status: string;
  data: { quote: DepositQuote };
}

/**
 * `POST /api/me/deposit/intent` のレスポンス。
 *
 * `client_secret` を Payment Element に渡して `stripe.confirmPayment` する。
 * `amount` は Stripe 請求額（= `quote.total`）。
 */
export interface DepositIntentResponse {
  status: string;
  data: {
    intent_id: string;
    client_secret: string;
    amount: string;
    currency: string;
    status_value: string;
    publishable_key: string | null;
    quote: DepositQuote;
  };
}

export const depositApi = createApi({
  reducerPath: "depositApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => ({
    /**
     * チャージモーダルの内訳表示用の見積り。
     * intent 作成と同じ計算モジュールを通るため、表示額 = 請求額が保証される。
     */
    getDepositQuote: builder.query<DepositQuoteResponse, { amount: number }>({
      query: ({ amount }) => `/api/me/deposit/quote?amount=${amount}`,
    }),

    /**
     * Stripe PaymentIntent を作成する。`amount` は「チャージ額（税抜）」。
     * カードへの請求額はサーバー側で利用料 + 消費税を上乗せした合計になる。
     */
    createDepositIntent: builder.mutation<
      DepositIntentResponse,
      { amount: number }
    >({
      query: ({ amount }) => ({
        url: "/api/me/deposit/intent",
        method: "POST",
        body: { amount },
      }),
    }),
  }),
});

export const { useGetDepositQuoteQuery, useCreateDepositIntentMutation } =
  depositApi;
